/**
 * TaxReportSignPage — Batch #14.239
 *
 * Page PUBLIQUE (magic link, aucune connexion) : /rapport-impot/:token
 * Le client consulte son rapport d'impôt (PDF fidèle au gabarit DG) puis signe.
 * La date est posée AUTOMATIQUEMENT sur le PDF au moment de la signature.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileSignature, CheckCircle2, Loader2, Download } from "lucide-react";
import { toast } from "@/lib/toast";
import { SignatureCanvas } from "@/pages/public/FiscalMandatePage";
import PdfFrame from "@/components/shared/PdfFrame";
import { API } from "@/api";

const NAVY = "#072354";

export default function TaxReportSignPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [info, setInfo] = useState(null);
  const [signature, setSignature] = useState("");
  const [hasSignature, setHasSignature] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/tax-report/${token}`);
        setInfo(r.data);
        if (r.data.status === "completed") setDone(true);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const pdfUrl = `${API}/public/tax-report/${token}/pdf`;

  const sign = async () => {
    if (!hasSignature) { toast.error("Veuillez signer dans le cadre"); return; }
    if (!consent) { toast.error("Veuillez confirmer la remise des documents"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/tax-report/${token}/sign`, {
        signature_image_b64: signature,
        consent: true,
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la signature — réessayez");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center max-w-md" data-testid="tax-report-not-found">
          <FileSignature className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <h1 className="text-lg font-bold text-slate-800 mb-1">Lien invalide</h1>
          <p className="text-sm text-slate-500">Ce lien n'existe pas ou n'est plus disponible. Contactez DG Comptabilité au 418 376-4298.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="tax-report-sign-page">
      <div className="text-white py-6 px-4" style={{ background: NAVY }}>
        <div className="max-w-3xl mx-auto">
          <h1 className="text-xl sm:text-2xl font-bold">DG Comptabilité</h1>
          <p className="text-sm opacity-80 mt-0.5">
            Rapport d'impôt {info.tax_year} — {info.client_name}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-6 space-y-4">
        {done ? (
          <div className="bg-white rounded-2xl border-2 border-emerald-200 p-8 text-center" data-testid="tax-report-done">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500 mb-3" />
            <h2 className="text-xl font-bold text-slate-800 mb-1">Merci !</h2>
            <p className="text-sm text-slate-600 mb-4">
              Votre confirmation a bien été enregistrée avec votre signature et la date du jour.
            </p>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold"
              style={{ background: NAVY }} data-testid="tax-report-download-signed">
              <Download className="w-4 h-4" /> Télécharger mon rapport signé
            </a>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              Bonjour {info.client_name} 👋 — voici votre rapport d'impôt {info.tax_year}. Consultez-le
              ci-dessous, puis signez pour confirmer la remise des documents.
            </p>
            {info.notes && <p className="text-sm text-slate-500 italic bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">« {info.notes} »</p>}

            {/* Aperçu PDF */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-2 border-b flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-600">Votre rapport (2 pages)</span>
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                  className="text-xs underline" style={{ color: NAVY }} data-testid="tax-report-open-pdf">
                  Ouvrir / télécharger le PDF
                </a>
              </div>
              <PdfFrame title="Rapport d'impôt" src={pdfUrl} height="70vh" accent={NAVY} label="Ouvrir mon rapport (PDF)" testId="tax-report-iframe" />
            </div>

            {/* Signature */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
              <h3 className="text-sm font-bold text-slate-800">Confirmation de remise des documents</h3>
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-blue-900" data-testid="tax-report-consent" />
                <span>
                  Je reconnais avoir bien reçu les documents mentionnés et confirme que les explications
                  concernant le rapport d'impôt m'ont été fournies.
                </span>
              </label>
              <SignatureCanvas onChange={(dataUrl, has) => { setSignature(dataUrl); setHasSignature(has); }} />
              <p className="text-[11px] text-slate-400">
                La date du jour sera inscrite automatiquement sur le document au moment de la signature.
              </p>
              <button onClick={sign} disabled={submitting || !hasSignature || !consent}
                className="w-full sm:w-auto px-6 py-3 rounded-xl text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40"
                style={{ background: NAVY }} data-testid="tax-report-sign-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                {submitting ? "Signature en cours..." : "Signer et confirmer"}
              </button>
            </div>
          </>
        )}
        <p className="text-[11px] text-slate-400 text-center pt-4">
          418 376-4298 · d.gougeon@outlook.fr · 412, rue J-R. Théberge, Chicoutimi, QC, G7J 3R9
        </p>
      </div>
    </div>
  );
}
